import type React from "react";
import { useEffect, useState } from "react";
import StepProgressBar from "../../progress-bar";
import type { SelectedAddress } from "../../../types/selectedAddress";
import { Button } from "../../misc/Button";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomToast from "../../misc/CustomToast";
import { getAuthHeader } from "../../../utils/authHeader";
import axios from "axios";
import {
    Features,
    ComfirmListing,
    UserListing,
    ListingType,
} from "../../../types/userListing";
import LoadingScreen from "../../../screens/LoadingScreen";
import LeafletMap from "../LeafletMap";
import HiddenAddressMapPlaceholder from "../../misc/HiddenAddressMapPlaceholder";
import { FaBath, FaCalendarDays, FaLocationDot, FaStar } from "react-icons/fa6";
import { BsHousesFill } from "react-icons/bs";
import { FaBed, FaRegClock, FaScroll, FaToilet } from "react-icons/fa";
import { MdOutlinePriceChange, MdSquareFoot } from "react-icons/md";
import { PiMapPinSimpleAreaFill } from "react-icons/pi";
import { compileAddress } from "../../../utils/addressCompiler";
import { useNavigate } from "react-router-dom";
import InputField from "../../misc/InputField";
import { Badge } from "../../calendar/Badge";
import type { MLSFeatures } from "../types";
import DataLoadingError from "../../misc/DataLoadingError";
import { useIsMobile } from "../../../hooks/useIsMobile";
import StatusOverlay from "../../misc/StatusOverlay";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import { setUser } from "../../../store/authSlice";
import type { User } from "../../../types/user";
import { Label } from "../../misc/Label";
import PlanLimitReached from "../../misc/PlanLimitReached";
import AutopilotOwnListingBlocked from "../../misc/AutopilotOwnListingBlocked";
import AutopilotTypeNotSyncedBlocked from "../../misc/AutopilotTypeNotSyncedBlocked";
import {
    getBlockedPropertyTypeLabel,
    isAutopilotOwnListingBlock,
    isAutopilotTypeNotSyncedBlock,
    isAutopilotUnderContractBlock,
    isMlsDisplayBlock,
    getMlsFeedOutageMessage,
} from "../../../utils/apiError";
import { useAutopilotEnabled } from "../../../hooks/useAutopilotEnabled";
import PropertyFeatureSelector from "./FeatureSelector";
import ReconfirmOverlay from "../../misc/ReconfirmOverlay";
import AddressCandidatePicker from "./AddressCandidatePicker";
import type { AddressCandidate } from "../../../types/addressCandidate";
import { isAddressCandidatesResponse } from "../../../utils/addressCandidateCopy";
import { getChanges } from "../listing/getChanges";
import { mlsListingTypeLabel, mlsSaleType } from "../../../utils/mlsListingType";

interface CreateUserListingProps {
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
    selectedAddress: SelectedAddress | null;
    mlsApproved: boolean | null;
    mlsId: string;
    listing: UserListing | null;
    setCreatedListing: React.Dispatch<React.SetStateAction<UserListing | null>>;
    saleType?: "sale" | "rent";
    setSaleType: React.Dispatch<React.SetStateAction<"sale" | "rent">>;
    listingType?: ListingType;
    sharedListing: boolean;
    setSharedListing: React.Dispatch<React.SetStateAction<boolean>>;
    setSaveMlsAgentDetails: React.Dispatch<React.SetStateAction<boolean>>;
}

const TEN_THOUSAND = 10000;

// MB-1430: the two reasons an MLS listing can't be imported. Same shape as the
// status overlay takes, so the preview gate and both 409 handlers share it.
const MLS_INTERNET_DISPLAY_BLOCK_COPY = {
    title: "Listing Can't Be Displayed Online",
    message:
        "Your MLS has this listing marked as not displayable on the internet, so it can't be imported or marketed on social media.",
    messageTwo:
        "Update the listing in the MLS to allow internet display, then try again.",
};

// An address-hidden listing imports; the agent acknowledges what will be
// shown (the feed withholds the street and the coordinates).
const MLS_ADDRESS_HIDDEN_COPY = {
    title: "Address Hidden by MLS",
    messageTwo: "Do you want to proceed with creating the listing?",
};

const ConfirmUserListing: React.FC<CreateUserListingProps> = ({
    setCurrentStep,
    selectedAddress,
    mlsApproved,
    mlsId,
    listing,
    setCreatedListing,
    saleType,
    setSaleType,
    sharedListing,
    setSharedListing,
    setSaveMlsAgentDetails,
}) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const user: User | null = useAppSelector((state) => state.auth.user);

    // MB-1472: leaseMonthlyDisplay is only set when the api classified an
    // explicit monthly rate. When that rate is a different number from
    // currentPrice the record is offered both ways, so neither number can be
    // shown alone under a bare "Price" label.
    const carriesBothPrices =
        listing?.leaseMonthlyDisplay != null &&
        listing?.currentPrice != null &&
        listing.leaseMonthlyDisplay !== listing.currentPrice;

    // A known lease rate always reads as "Lease Price", whether or not the
    // record also carries a sale price. An unclassifiable one keeps "Price"
    // plus the For Lease badge - we cannot say what that number measures.
    const priceRowLabel = carriesBothPrices
        ? "Sale Price"
        : listing?.leaseMonthlyDisplay != null &&
          listing?.leaseMonthlyDisplay === listing?.currentPrice
        ? "Lease Price"
        : "Price";

    // MB-1441: an Active Under Contract listing cannot be added while
    // Autopilot is on - the status overlay hard-blocks instead of warning.
    const { autopilotEnabled, loading: autopilotLoading } =
        useAutopilotEnabled();

    const [loading, setLoading] = useState<boolean>(listing ? false : true);
    const [error, setError] = useState<boolean>(false);
    // A pick re-runs the preview by number from local state; the parent's mlsId
    // stays empty because step 1 refuses "MLS number and address both filled".
    const [candidates, setCandidates] = useState<AddressCandidate[] | null>(
        null
    );
    const [pickedMlsId, setPickedMlsId] = useState<string>("");
    const showCandidatePicker = candidates !== null && !listing && !loading;
    const [originalListing, setOriginalListing] = useState<UserListing | null>(null);
    const [statusAcknowledged, setStatusAcknowledged] =
        useState<boolean>(false);
    const [overlay, setOverlay] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        messageTwo?: string;
        allowNext: boolean;
    }>({
        isOpen: false,
        title: "",
        message: "",
        messageTwo: "",
        allowNext: false,
    });
    const [saleTypeOverlay, setSaleTypeOverlay] = useState<boolean>(false);
    const [sharedAgentOverlay, setSharedAgentOverlay] =
        useState<boolean>(false);
    // Case A — first capture (no saved agent id): "save my MLS details?" 3-button.
    const [saveDetailsOverlay, setSaveDetailsOverlay] =
        useState<boolean>(false);
    const [planLimitOpen, setPlanLimitOpen] = useState<boolean>(false);
    const [descPromptOpen, setDescPromptOpen] = useState<boolean>(false);
    // MB-1424: hard block - the user's own listing while Autopilot is on.
    const [autopilotBlockOpen, setAutopilotBlockOpen] =
        useState<boolean>(false);
    // MB-1506: same block, but the property type is not auto-synced and the
    // listing is nowhere in the review lists - so it needs the how-to copy.
    const [typeNotSyncedOpen, setTypeNotSyncedOpen] = useState<boolean>(false);
    const [blockedPropertyType, setBlockedPropertyType] = useState<
        string | null
    >(null);
    const isMobile = useIsMobile(900);

    // Saved MLS agent id — present means the user already captured their MLS
    // identity, which gates the first-add "save my MLS details?" prompt.
    const savedAgentId = (user?.listAgentMlsId || "").trim();

    // Initialize originalListing when listing prop has an _id (already created)
    useEffect(() => {
        if (listing?._id && !originalListing) {
            setOriginalListing(listing);
        }
    }, [listing, originalListing]);

    // The api derives sale-vs-lease from the MLS PropertyType and ignores the
    // wizard's pick, so send the derived value instead of the "sale" default.
    useEffect(() => {
        if (listing?.mlsPropertyType) {
            setSaleType(mlsSaleType(listing.mlsPropertyType));
        }
    }, [listing?.mlsPropertyType, setSaleType]);

    useEffect(() => {
        const fetchListingData = async () => {
            if (listing) return;
            try {
                setLoading(true);
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                let payload;

                if (mlsApproved) {
                    if (pickedMlsId) {
                        payload = { mlsId: pickedMlsId };
                    } else if (mlsId) {
                        payload = { mlsId };
                    } else if (selectedAddress) {
                        payload = { address: selectedAddress };
                    }
                } else {
                    payload = { address: compileAddress(selectedAddress!) };
                }

                const response = await axios.post(
                    `${backendUrl}/listing`,
                    payload,
                    {
                        headers,
                        withCredentials: true,
                    }
                );

                if (response.status === 200) {
                    setLoading(false);
                    if (isAddressCandidatesResponse(response.data)) {
                        setCandidates(response.data.candidates);
                        return;
                    }
                    // Clear description as per requirements - ignore description from rentcast
                    if (mlsApproved) {
                        setCreatedListing(response.data);
                    } else {
                        const listingData = {
                            ...response.data,
                            legalDescription: "",
                            currentPrice: undefined,
                        };

                        setCreatedListing(listingData);
                    }

                    // A hidden address is "City, ST ZIP": geocoding it would pin
                    // the city centre and that pin would be stored on create.
                    // An MLS preview already carries the pin the import will
                    // store (none when Google cannot place the street), so only
                    // a Rentcast preview is geocoded here.
                    if (
                        !mlsApproved &&
                        (!response.data.latitude || !response.data.longitude) &&
                        response.data.formattedAddress &&
                        response.data.addressDisplayAllowed !== false
                    ) {
                        try {
                            const apiKey =
                                process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
                            if (!apiKey) {
                                throw new Error(
                                    "Google Geocoding API key is not set."
                                );
                            }
                            const geocodingUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
                                response.data.formattedAddress
                            )}&key=${apiKey}`;
                            const geocodingResponse = await axios.get(
                                geocodingUrl
                            );
                            if (geocodingResponse.data.status === "OK") {
                                const location =
                                    geocodingResponse.data.results[0].geometry
                                        .location;
                                // Update the listing with new coordinates
                                const updatedListing: UserListing = {
                                    ...response.data,
                                    latitude: location.lat,
                                    longitude: location.lng,
                                    legalDescription: mlsApproved
                                        ? response.data.legalDescription ?? ""
                                        : "",
                                    currentPrice: undefined,

                                };
                                setCreatedListing(updatedListing);
                            }
                        } catch (error) {
                            console.error(
                                "Error fetching coordinates from Google:",
                                error
                            );
                            toast.error(
                                <CustomToast
                                    message="Failed to fetch coordinates."
                                    type="error"
                                />,
                                { autoClose: 3000 }
                            );
                        }
                    }
                } else {
                    setLoading(false);
                    console.error("Failed to fetch listing data:", response);
                    toast.error(
                        <CustomToast
                            message="Unable to fetch property details. Please check the address and try again."
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                    setTimeout(() => {
                        setCurrentStep(1);
                    }, 3000);
                }
            } catch (error: unknown) {
                setLoading(false);
                const feedOutage = getMlsFeedOutageMessage(error);
                if (feedOutage) {
                    toast.error(
                        <CustomToast message={feedOutage} type="error" />,
                        { autoClose: 6000 }
                    );
                    setTimeout(() => {
                        setCurrentStep(1);
                    }, 3000);
                    return;
                }
                if ((error as any).response.status === 410) {
                    setPlanLimitOpen(true);
                }
                console.error("Error fetching listing data:", error);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchListingData();
    }, [
        selectedAddress,
        setCurrentStep,
        mlsId,
        pickedMlsId,
        mlsApproved,
        setCreatedListing,
        listing,
    ]);

    const [progressSteps] = useState([
        { label: "Property Address", isActive: true, isCompleted: true },
        { label: "Confirm & Edit Details", isActive: true, isCompleted: false },
    ]);

    const handleBack = () => {
        setCurrentStep(1);
    };

    const continueSave = async (saveMlsAgentDetailsOverride?: boolean) => {
        if (mlsApproved) {
            if (
                listing?.legalDescription &&
                listing?.legalDescription?.length >= 500
            ) {
                try {
                    setLoading(true);
                    const headers = await getAuthHeader();
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                    const payload = {
                        mlsId: mlsId || listing?._id,
                        footer: listing?.footer,
                        latitude: listing?.latitude,
                        longitude: listing?.longitude,
                        formattedAddress: listing?.formattedAddress,
                        saleType,
                        sharedListing,
                        saveMlsAgentDetails: saveMlsAgentDetailsOverride ?? false,
                    };
                    const response = await axios.post<{
                        mlsSetupPending?: number;
                    }>(`${backendUrl}/listing/create`, payload, {
                        headers,
                        withCredentials: true,
                    });

                    if (response.status === 200 || response.status === 201) {
                        // When MLS details were saved, the user doc gained the
                        // agent identity + autoIngestEnabled — refetch so Redux
                        // (and the MLS Setting page) reflect it without a reload.
                        if (saveMlsAgentDetailsOverride) {
                            try {
                                const fresh = await axios.get(
                                    `${backendUrl}/settings/personal-info`,
                                    { headers }
                                );
                                dispatch(setUser({ ...user, ...fresh.data }));
                            } catch {
                                if (user) {
                                    dispatch(
                                        setUser({ ...user, mlsWelcome: false })
                                    );
                                }
                            }
                        } else if (user) {
                            dispatch(setUser({ ...user, mlsWelcome: false }));
                        }

                        setLoading(false);
                        // MB-1442: a shared listing under Autopilot needs no
                        // campaign setup - adoption schedules it once the
                        // import settles, so say so instead of leaving the
                        // user hunting for a Create Campaign button that
                        // MB-1441 hides.
                        const autopilotWillSchedule =
                            sharedListing && autopilotEnabled === true;
                        toast.success(
                            <CustomToast
                                message={
                                    autopilotWillSchedule
                                        ? "Listing creation initiated. Auto Pilot will schedule posts for this listing automatically."
                                        : "Listing creation initiated."
                                }
                                type="success"
                            />,
                            { autoClose: autopilotWillSchedule ? 6000 : 3000 }
                        );
                        // First-time Save & Auto-Sync always opens the setup wizard, even when
                        // the default-type lookup found nothing: its first step asks for the
                        // property types, which is how a commercial-only agent gets their listings.
                        navigate(
                            "/listings",
                            saveMlsAgentDetailsOverride
                                ? { state: { openMlsReview: "setup" } }
                                : undefined
                        );
                    } else if (response.status === 410) {
                        // Plan limit reached – show popup instead of toast
                        setLoading(false);
                        setPlanLimitOpen(true);
                    } else {
                        throw new Error(
                            "Failed to initiate listing creation. Please try again."
                        );
                    }
                } catch (error) {
                    const mlsDisplayBlocked = isMlsDisplayBlock(error);
                    const feedOutage = getMlsFeedOutageMessage(error);
                    if (feedOutage) {
                        setLoading(false);
                        toast.error(
                            <CustomToast message={feedOutage} type="error" />,
                            { autoClose: 6000 }
                        );
                        return;
                    } else if ((error as any).response.status === 410) {
                        setLoading(false);
                        setPlanLimitOpen(true);
                        return;
                    } else if (isAutopilotOwnListingBlock(error)) {
                        setLoading(false);
                        setAutopilotBlockOpen(true);
                        return;
                    } else if (isAutopilotTypeNotSyncedBlock(error)) {
                        setLoading(false);
                        setBlockedPropertyType(
                            getBlockedPropertyTypeLabel(error)
                        );
                        setTypeNotSyncedOpen(true);
                        return;
                    } else if (isAutopilotUnderContractBlock(error)) {
                        // MB-1441: the preview overlay normally blocks this
                        // earlier - defense in depth for stale previews.
                        setLoading(false);
                        setOverlay({
                            isOpen: true,
                            title: "Listing Under Contract",
                            message:
                                "This listing is Active Under Contract. Auto Pilot doesn't market under-contract listings, so it can't be added while Auto Pilot is on.",
                            messageTwo: "",
                            allowNext: false,
                        });
                        return;
                    } else if (mlsDisplayBlocked) {
                        // MB-1430: the preview overlay normally blocks this
                        // earlier - defense in depth for stale previews.
                        setLoading(false);
                        setOverlay({
                            isOpen: true,
                            ...MLS_INTERNET_DISPLAY_BLOCK_COPY,
                            allowNext: false,
                        });
                        return;
                    } else {
                        setLoading(false);
                        console.error(
                            "Error initiating listing creation:",
                            error
                        );
                        toast.error(
                            <CustomToast
                                message="Failed to initiate listing creation. Please try again."
                                type="error"
                            />,
                            {
                                autoClose: 3000,
                            }
                        );
                    }
                }
            } else {
                setCreatedListing(listing);
                setCurrentStep(4);
            }
        } else {
            handleNONMLSContinue();
        }
    };

    const handleNONMLSContinue = async () => {
        // Ensure price is present for non-MLS listings before proceeding
        if (!mlsApproved && (!listing?.currentPrice || listing.currentPrice <= 0)) {
            scrollToField("currentPrice");
            return;
        }

        // If listing already has an _id, it means it was already created
        if (listing?._id) {
            // Check if listing was modified by comparing with original
            const hasChanges = originalListing && JSON.stringify(listing) !== JSON.stringify(originalListing);
            
            if (hasChanges) {
                // Get only the changed fields
                const changes = originalListing ? getChanges(originalListing, listing) : {};
                
                // If no actual changes, just advance to next step
                if (Object.keys(changes).length === 0) {
                    setCurrentStep(3);
                    return;
                }
                
                // Make PATCH call to update the listing
                try {
                    setLoading(true);
                    const headers = await getAuthHeader();
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                    
                    const response = await axios.patch(
                        `${backendUrl}/listing`,
                        {
                            ...changes,
                            listingId: listing._id,
                            saleType,
                            sharedListing,
                            auto: false
                        },
                        {
                            headers,
                            withCredentials: true,
                        }
                    );

                    if (response.status === 200) {
                        setLoading(false);
                        setCreatedListing(response.data);
                        setOriginalListing(response.data); // Update original with new data
                        toast.success(
                            <CustomToast
                                message="Listing updated successfully."
                                type="success"
                            />,
                            { autoClose: 3000 }
                        );
                        setCurrentStep(3);
                    } else {
                        throw new Error("Failed to update listing");
                    }
                } catch (error) {
                    setLoading(false);
                    console.error("Error updating listing:", error);
                    toast.error(
                        <CustomToast
                            message="Failed to update listing. Please try again."
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                }
            } else {
                // No changes, just advance to next step
                setCurrentStep(3);
            }
            return;
        }

        // Original POST logic for new listings
        try {
            setLoading(true);
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const payload = { ...listing, saleType, sharedListing };
            const response = await axios.post(
                `${backendUrl}/listing/create`,
                payload,
                {
                    headers,
                    withCredentials: true,
                }
            );

            if (response.status === 201 || response.status === 200) {
                if (user) {
                    dispatch(setUser({ ...user, mlsWelcome: false }));
                }

                setLoading(false);

                setCreatedListing(response.data);
                setOriginalListing(response.data); // Store original for comparison
                setCurrentStep(3);
            } else {
                if ((error as any).response.status === 410) {
                    setLoading(false);
                    setPlanLimitOpen(true);
                    return;
                }

                setLoading(false);
                toast.error(
                    <CustomToast
                        message="Failed to create listing. Please try again."
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
                setCurrentStep(2);
            }
        } catch (error) {
            const mlsDisplayBlocked = isMlsDisplayBlock(error);
            if ((error as any).response.status === 410) {
                setLoading(false);
                setPlanLimitOpen(true);
                return;
            }
            if (isAutopilotOwnListingBlock(error)) {
                setLoading(false);
                setAutopilotBlockOpen(true);
                return;
            }
            if (isAutopilotTypeNotSyncedBlock(error)) {
                setLoading(false);
                setBlockedPropertyType(getBlockedPropertyTypeLabel(error));
                setTypeNotSyncedOpen(true);
                return;
            }
            if (isAutopilotUnderContractBlock(error)) {
                // MB-1441: defense in depth behind the preview overlay.
                setLoading(false);
                setOverlay({
                    isOpen: true,
                    title: "Listing Under Contract",
                    message:
                        "This listing is Active Under Contract. Auto Pilot doesn't market under-contract listings, so it can't be added while Auto Pilot is on.",
                    messageTwo: "",
                    allowNext: false,
                });
                return;
            }
            if (mlsDisplayBlocked) {
                // MB-1430: defense in depth behind the preview overlay.
                setLoading(false);
                setOverlay({
                    isOpen: true,
                    ...MLS_INTERNET_DISPLAY_BLOCK_COPY,
                    allowNext: false,
                });
                return;
            }

            setLoading(false);
            console.error("Error creating listing:", error);
            toast.error(
                <CustomToast
                    message="Failed to create listing. Please try again."
                    type="error"
                />,
                { autoClose: 3000 }
            );
            setCurrentStep(2);
        }
    };

    // MB-1430: the preview endpoint reports internet-display compliance.
    // An explicit false on the entire-listing flag is a hard block - the
    // create call would refuse anyway, so warn before any work is done.
    // An explicit false on the address flag is NOT a block: the listing
    // imports as "City, ST ZIP"; the agent acknowledges that first.
    const mlsCompliance = listing as {
        internetDisplayAllowed?: boolean;
        addressDisplayAllowed?: boolean;
    };
    const isMlsListing = listing?.listingType === ListingType.MLSListing;
    const internetDisplayBlocked =
        isMlsListing && mlsCompliance?.internetDisplayAllowed === false;
    const addressHidden =
        isMlsListing && mlsCompliance?.addressDisplayAllowed === false;
    const importBlocked = internetDisplayBlocked;

    useEffect(() => {
        if (
            !loading &&
            listing &&
            ((listing.listingType === ListingType.MLSListing &&
                listing.mlsStatus !== "Active") ||
                listing?.exists === true ||
                importBlocked ||
                addressHidden) &&
            !statusAcknowledged
        ) {
            const status =
                listing.listingType === ListingType.RentcastListing
                    ? "EXISTS"
                    : listing.mlsStatus || "";
            let title = "";
            let message = "";
            let messageTwo = "";
            let allowNext = false;
            if (importBlocked && status !== "EXISTS") {
                title = MLS_INTERNET_DISPLAY_BLOCK_COPY.title;
                message = MLS_INTERNET_DISPLAY_BLOCK_COPY.message;
                messageTwo = MLS_INTERNET_DISPLAY_BLOCK_COPY.messageTwo;
                allowNext = false;
            } else if (status === "Active" && addressHidden) {
                // Warn-and-proceed. A non-Active status below takes precedence;
                // the preview shows the "City, ST ZIP" either way.
                title = MLS_ADDRESS_HIDDEN_COPY.title;
                message = `Your MLS hides this listing's address, so it will be marketed as ${
                    listing.formattedAddress || "its city and ZIP"
                } with no street and no map pin. Published posts say "Undisclosed Address".`;
                messageTwo = MLS_ADDRESS_HIDDEN_COPY.messageTwo;
                allowNext = true;
            } else if (status === "EXISTS") {
                title = "Listing Already Exists";
                message =
                    "This listing already exists in your account. You must delete the existing listing before creating the same listing again.";
                allowNext = false;
            } else if (status === "ComingSoon") {
                title = "Coming Soon Listing";
                message =
                    "This listing is marked as Coming Soon. Posts from this campaign will mention that this is a Coming Soon listing.";
                allowNext = true;
            } else if (status === "ActiveUnderContract") {
                // MB-1441: hard block under Autopilot (the api refuses with
                // AUTOPILOT_UNDER_CONTRACT_LISTING anyway); the classic
                // warn-and-proceed only when Autopilot is off or unknown.
                // A still-loading flag is null, which reads as "off" and
                // would flash warn-and-proceed with a live Next before the
                // answer lands. Hold the overlay until the request settles;
                // a failed fetch settles to null and keeps warn-and-proceed.
                if (autopilotLoading) {
                    return;
                }
                if (autopilotEnabled === true) {
                    title = "Listing Under Contract";
                    message =
                        "This listing is Active Under Contract. Auto Pilot doesn't market under-contract listings, so it can't be added while Auto Pilot is on.";
                    allowNext = false;
                } else {
                    title = "Listing Under Contract";
                    message = "This listing is currently under contract.";
                    messageTwo =
                        "Do you want to proceed with creating the listing?";
                    allowNext = true;
                }
            } else if (status === "Closed") {
                title = "Listing Closed";
                message =
                    "This listing is closed. If the property was recently relisted, the new listing can take time to appear in the MLS feed - try again later, or import it using the MLS number. If you want to create a congratulations/sold post, please go to the Schedule and create an Ad Hoc post.";
                allowNext = false;
            } else if (
                [
                    "Canceled",
                    "Delete",
                    "Expired",
                    "Incomplete",
                    "Withdrawn",
                    "Hold",
                    "Pending",
                ].includes(status)
            ) {
                title = "Invalid Listing Status";
                message = `This listing is marked as ${status}.`;
                messageTwo = "Please update the MLS before proceeding.";
                allowNext = false;
            } else {
                title = "Invalid Listing Status";
                message = `This listing has an invalid status: ${status}.`;
                messageTwo = "Please update the MLS before proceeding.";
                allowNext = false;
            }

            setOverlay({
                isOpen: true,
                title,
                message,
                messageTwo,
                allowNext,
            });
        }
    }, [loading, listing, statusAcknowledged, importBlocked, internetDisplayBlocked, addressHidden, autopilotEnabled, autopilotLoading]);

    // Show the agent-confirmation flow (Confirm Agent Details → Case A/B) when
    // needed, otherwise create directly. Shared by the main save path and the
    // "Update Listing Type" overlay so both honor the MLS agent check (Rentcast
    // users only see it for shared-agent listings).
    const proceedWithAgentCheck = (): void => {
        const footerSharedAgent = !!(
            listing?.footer?.sharedAgentName ||
            listing?.footer?.sharedAgentAgency
        );
        if (
            !sharedListing &&
            (mlsApproved
                ? footerSharedAgent || !savedAgentId
                : footerSharedAgent)
        ) {
            setSharedAgentOverlay(true);
            return;
        }
        continueSave();
    };

    const handleSaveAndNext = async () => {
        // Check if description is filled (validate topmost field first)
        if (
            !mlsApproved &&
            (!listing?.legalDescription ||
                listing?.legalDescription?.trim() === "")
        ) {
            // Scroll to description field
            return scrollToDescription();
        }

        // Require price for non-MLS listings - CHECK THIS BEFORE LENGTH WARNING
        if (
            !mlsApproved &&
            (!listing?.currentPrice || listing.currentPrice <= 0)
        ) {
            scrollToField("currentPrice");
            return;
        }

        // Only check description length AFTER both fields are filled
        if (
            !mlsApproved &&
            listing?.legalDescription &&
            listing.legalDescription.trim().length > 0 &&
            listing.legalDescription.trim().length < 1300
        ) {
            setDescPromptOpen(true);
            return;
        }

        // Price sanity check is for non-MLS listings only: an MLS listing's
        // type comes from the feed, and the hidden toggle still defaults to
        // "sale", so every MLS rental would otherwise trip the low-price guard.
        if (
            !listing?.mlsPropertyType &&
            listing?.currentPrice &&
            listing?.currentPrice > 0
        ) {
            if (saleType === "rent" && listing?.currentPrice > TEN_THOUSAND) {
                setSaleTypeOverlay(true);
                return;
            } else if (
                saleType === "sale" &&
                listing?.currentPrice <= TEN_THOUSAND
            ) {
                setSaleTypeOverlay(true);
                return;
            }
        }

        if (
            listing?.mlsStatus === "Active" ||
            statusAcknowledged ||
            !mlsApproved
        ) {
            proceedWithAgentCheck();
        }
    };

    const scrollToField = (
        name: string,
        silent: boolean = false,
        customMessage?: string
    ) => {
        const element = document.querySelector(`[name="${name}"]`);
        if (element) {
            element.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
            (element as HTMLElement).focus();
        }
        if (!silent) {
            const defaultMessage =
                name === "legalDescription"
                    ? "Please fill in the property description to continue."
                    : name === "currentPrice"
                    ? "Please enter a price to continue."
                    : "Please fill in the required field to continue.";
            toast.error(
                <CustomToast
                    message={customMessage ?? defaultMessage}
                    type="error"
                />,
                { autoClose: 5000 }
            );
        }
        return;
    };

    const scrollToDescription = (silent: boolean = false) =>
        scrollToField("legalDescription", silent);

    const booleanFields: Set<string> = new Set([
        "cooling",
        "fireplace",
        "garage",
        "heating",
        "pool",
        "accessibility",
        "basement",
        "spa",
        "view",
        "waterfront",
    ]);

    const typeOrAssociatedFields: Set<string> = new Set([
        "coolingType",
        "fireplaceType",
        "garageType",
        "heatingType",
        "poolType",
        "viewType",
        "accessibilityType",
        "basementType",
        "spaFeatures",
        "waterfrontFeatures",
    ]);

    const booleanToFieldsMap: Record<string, string[]> = {
        cooling: ["coolingType"],
        fireplace: ["fireplaceType", "fireplaceFeatures"],
        garage: ["garageType", "garageSpaces"],
        heating: ["heatingType"],
        pool: ["poolType"],
        accessibility: ["accessibilityType"],
        basement: ["basementType"],
        spa: ["spaFeatures"],
        view: ["viewType"],
        waterfront: ["waterfrontFeatures"],
    };

    const fieldToBooleanMap: Record<string, string> = {
        coolingType: "cooling",
        fireplaceType: "fireplace",
        fireplaceFeatures: "fireplace",
        garageType: "garage",
        garageSpaces: "garage",
        heatingType: "heating",
        poolType: "pool",
        accessibilityType: "accessibility",
        basementType: "basement",
        spaFeatures: "spa",
        viewType: "view",
        waterfrontFeatures: "waterfront",
    };

    const formatKey = (key: string): string => {
        return key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    };

    const isBooleanField = (key: string): boolean => booleanFields.has(key);
    const isTypeOrAssociatedField = (key: string): boolean =>
        typeOrAssociatedFields.has(key);

    useEffect(() => {
        if (!mlsApproved) {
            if (listing?.fullBathrooms) {
                if (listing.fullBathrooms % 1 !== 0) {
                    const integerFullBathrooms = Math.floor(
                        listing.fullBathrooms
                    );
                    const newHalfBathrooms = (listing.halfBathrooms || 0) + 1;
                    setCreatedListing({
                        ...listing,
                        fullBathrooms: integerFullBathrooms,
                        halfBathrooms: newHalfBathrooms,
                    });
                }
            }
        }
    }, [mlsApproved, listing, setCreatedListing]);

    if (error) {
         if (planLimitOpen) {
            return (
                <PlanLimitReached
                    open={planLimitOpen}
                    onClose={() => {
                        setPlanLimitOpen(false);
                        navigate("/listings");
                    }}
                    type="listing"
                />
            );
        } else {
            return (
                <DataLoadingError
                    heading="Failed to fetch property details."
                    text={
                        mlsId || pickedMlsId
                            ? "We couldn’t find a match for the MLS Number you entered. It may be incorrect, newly added, or the listing might not be active yet. If it was recently listed, please allow up to 15 minutes for the information to update in our system."
                            : !mlsApproved
                            ? "We weren't able to find a property match for the address provided."
                            : "We weren't able to find a property match for the address provided. This can happen if the address has formatting differences, is a mailing address without a direct property record, or has not yet been added to public records."
                    }
                    textTwo={
                        mlsId || pickedMlsId
                            ? "Please double-check the MLS Number and listing status before trying again."
                            : !mlsApproved
                            ? "Please double-check the spelling, city, and ZIP code. Some new properties or recent changes may take time to appear."
                            : "Please double-check the spelling, city, and ZIP code. Some new properties or recent changes may take time to appear in available public records."
                    }
                    setCurrentStep={setCurrentStep}
                    step={1}
                />
            );
        }
    }

    const getSaleTypeOverlayMessage = () => {
        if (listing?.currentPrice !== undefined) {
            if (saleType === "rent" && listing.currentPrice > TEN_THOUSAND) {
                return 'The price seems higher than typical for a rental. You might want to double-check if "For Sale" is a better fit.';
            }
            if (saleType === "sale" && listing.currentPrice <= TEN_THOUSAND) {
                return 'The price is on the lower end, which is more common for rentals. Please make sure "For Sale" is the right choice.';
            }
        }

        return "Double-checking the listing type helps ensure the property is categorized accurately.";
    };
    const features = listing?.features;
    let featureKeys: string[] = [];
    let featureValues: (string | number | boolean)[] = [];
    let nonMLSStringFeatures: string[] = [];
    let nonMLSNumberFeatures: string[] = [];

    if (features) {
        Object.keys(features).forEach((key) => {
            featureKeys.push(key);
        });
        Object.values(features).forEach((value) => {
            featureValues.push(value);
        });

        featureKeys.forEach((key, index) => {
            if (typeof featureValues[index] === "string") {
                nonMLSStringFeatures.push(`${key}: ${featureValues[index]}`);
            } else if (typeof featureValues[index] === "number") {
                nonMLSNumberFeatures.push(`${key}: ${featureValues[index]}`);
            }
        });
    }              

    return (
      <main
           className={`flex min-h-screen overflow-auto flex-col justify-center items-center ${
                isMobile ? "bg-white box-border" : "bg-background"
            } shadow-[0px_4px_4px_rgba(0,0,0,0.25)] max-md:px-5`}
        
        // className={`flex overflow-auto flex-col justify-center items-center min-h-full shadow-[0px_4px_4px_rgba(0,0,0,0.25)] ${
        //   isMobile ? "px-0 bg-white" : "bg-background"
        // }`}
      >
        <StatusOverlay
          isOpen={overlay.isOpen}
          title={overlay.title}
          message={overlay.message}
          messageTwo={overlay.messageTwo}
          allowNext={overlay.allowNext}
          onNext={() => {
            setStatusAcknowledged(true);
            setOverlay((prev) => ({ ...prev, isOpen: false }));
          }}
          onBack={handleBack}
        />

        {/* Plan limit popup using PopoverContent */}
        {planLimitOpen && (
          <PlanLimitReached
            open={planLimitOpen}
            onClose={() => {
              setPlanLimitOpen(false);
              navigate("/listings");
            }}
            type="listing"
          />
        )}

        {/* MB-1424: own-listing hard block - Autopilot ingests these itself */}
        <AutopilotOwnListingBlocked
          open={autopilotBlockOpen}
          onClose={() => {
            setAutopilotBlockOpen(false);
            navigate("/listings");
          }}
        />

        {/* MB-1506: the type is switched off in MLS Agent Profile - the dialog
            walks the user to the setting that turns it on. */}
        <AutopilotTypeNotSyncedBlocked
          open={typeNotSyncedOpen}
          propertyTypeLabel={blockedPropertyType}
          onClose={() => {
            setTypeNotSyncedOpen(false);
            navigate("/listings");
          }}
          onGoToSettings={() => {
            setTypeNotSyncedOpen(false);
            navigate("/settings/mls-details");
          }}
        />

        {/* Sale Type Selection Overlay */}
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center ${
            saleTypeOverlay ? "block" : "hidden"
          }`}
        >
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-primary mb-4">
              Update Listing Type
            </h2>
            <p className="mb-4">{getSaleTypeOverlayMessage()}</p>

            <div className="flex flex-col my-3">
              <Label className="text-primary my-2">Listing Type</Label>
              <div className="flex gap-8">
                <button
                  type="button"
                  onClick={() => setSaleType("sale")}
                  className={`flex-1 py-1.5 px-4 rounded-md border ${
                    saleType === "sale"
                      ? "bg-secondary text-white border-secondary"
                      : "bg-white text-primary border-gray-300 hover:border-secondary"
                  }`}
                >
                  For Sale
                </button>
                <button
                  type="button"
                  onClick={() => setSaleType("rent")}
                  className={`flex-1 py-1.5 px-4 rounded-md border ${
                    saleType === "rent"
                      ? "bg-secondary text-white border-secondary"
                      : "bg-white text-primary border-gray-300 hover:border-secondary"
                  }`}
                >
                  For Rent
                </button>
              </div>
            </div>

            <div className="flex justify-between mt-6">
              <Button
                onClick={() => setSaleTypeOverlay(false)}
                className="w-full mr-2"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setSaleTypeOverlay(false);
                  proceedWithAgentCheck();
                }}
                className="w-full ml-2"
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center ${
            sharedAgentOverlay ? "block" : "hidden"
          } ${isMobile ? "px-4" : ""}`}
        >
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4">
            <h2
              className={`${isMobile ? "text-center" : ""} text-xl font-bold text-primary mb-4`}
            >
              Confirm Agent Details
            </h2>
            <p className="mb-4">
              {listing?.footer?.sharedAgentName ||
              listing?.footer?.sharedAgentAgency
                ? "It looks like this property may be listed by another agent."
                : "Please confirm the MLS agent details for this property."}
            </p>
            <div className="mb-4">
              <p>
                <strong>Agent Name:</strong>{" "}
                {listing?.footer?.sharedAgentName ||
                  listing?.agent?.listAgentFullName ||
                  "—"}
              </p>
              <p>
                <strong>Agency:</strong>{" "}
                {listing?.footer?.sharedAgentAgency ||
                  listing?.agent?.listOfficeName ||
                  "—"}
              </p>
              {listing?.agent?.listAgentMlsId && (
                <p>
                  <strong>MLS Agent ID:</strong> {listing.agent.listAgentMlsId}
                </p>
              )}
            </div>
            <p className="mb-6">
              Are you marketing this property on behalf of another agent?
            </p>

            <div
              className={`flex ${isMobile ? "flex-col w-full gap-2" : "flex-row justify-between w-full"} `}
            >
              <Button
                className={isMobile ? "w-full" : "w-full mr-2"}
                onClick={() => {
                  setSharedAgentOverlay(false);
                  // It's the user's own listing. For MLS users, capture their MLS
                  // details on first add (Case A); otherwise just proceed.
                  if (mlsApproved && !savedAgentId) {
                    setSaveDetailsOverlay(true);
                  } else {
                    continueSave();
                  }
                }}
              >
                No, I am the listing agent
              </Button>
              <Button
                className={isMobile ? "w-full" : "w-full ml-2"}
                onClick={() => {
                  setSharedListing(true);
                  setSharedAgentOverlay(false);
                }}
              >
                Yes, marketing for another agent
              </Button>
            </div>
          </div>
        </div>
        {saveDetailsOverlay && (
          <div
            className={`fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center ${
              isMobile ? "px-4" : ""
            }`}
          >
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
              <h2
                className={`${
                  isMobile ? "text-center" : ""
                } text-xl font-bold text-primary mb-4`}
              >
                Use these MLS details for future listings?
              </h2>
              <p className="mb-4">
                We found MLS agent information for this listing. Save these
                details to your profile to automatically import and sync future
                active listings from this MLS account.
              </p>
              <div className="mb-6 rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-primary space-y-1">
                <p>
                  <strong>MLS Agent ID:</strong>{" "}
                  {listing?.agent?.listAgentMlsId || "—"}
                </p>
                <p>
                  <strong>Agent Name:</strong>{" "}
                  {listing?.agent?.listAgentFullName || "—"}
                </p>
                <p>
                  <strong>Office Name:</strong>{" "}
                  {listing?.agent?.listOfficeName || "—"}
                </p>
                <p>
                  <strong>MLS System:</strong>{" "}
                  {listing?.agent?.listAOR || "—"}
                </p>
              </div>
              <div
                className={`flex ${
                  isMobile ? "flex-col w-full gap-2" : "flex-row gap-2"
                }`}
              >
                <Button
                  className="w-full"
                  onClick={() => {
                    setSaveMlsAgentDetails(true);
                    setSaveDetailsOverlay(false);
                    continueSave(true);
                  }}
                >
                  Save &amp; Auto-Sync Future Listings
                </Button>
                <Button
                  className="w-full"
                  onClick={() => {
                    setSaveMlsAgentDetails(false);
                    setSaveDetailsOverlay(false);
                    continueSave(false);
                  }}
                >
                  Import This Listing Only
                </Button>
                <Button
                  className="w-full"
                  onClick={() => setSaveDetailsOverlay(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
        <section
          className={`flex flex-col items-center ${
                    isMobile ? "py-4 min-h-screen box-border" : "px-8 py-12"
                } bg-white w-full  max-w-3xl max-md:px-4 max-md:py-8 relative`}

          // className={`flex flex-col items-center ${
          //   isMobile
          //     ? "py-6 px-1 pb-4 max-w-sm"
          //     : "py-20 w-[905px] px-14"
          // } bg-white rounded-2xl h-full relative`}
        >
          {candidates && showCandidatePicker ? (
            <AddressCandidatePicker
              address={
                selectedAddress
                  ? `${selectedAddress.address1}, ${selectedAddress.city}, ${selectedAddress.state} ${selectedAddress.zipCode}`
                  : candidates[0]?.address ?? ""
              }
              candidates={candidates}
              onPick={(picked) => {
                setCandidates(null);
                setPickedMlsId(picked);
              }}
              onBack={handleBack}
            />
          ) : loading || !listing ? (
            <LoadingScreen />
          ) : (
            <>
              <header className="flex flex-col mx-auto w-full ">
                <h1 className={`my-2 text-primary ${isMobile ? "text-base" : "text-2xl"} font-bold leading-none text-center`}>
                  Confirm & Edit Property Details
                </h1>
                <StepProgressBar steps={progressSteps.filter((step) => step)} />
              </header>
              <div
                className={`${
                  isMobile ? "max-w-sm mx-auto px-1" : "w-full max-w-3xl"
                } mt-4 rounded-lg border bg-card text-card-foreground shadow-sm text-primary`}
              >
                <div className={`flex flex-col space-y-1.5 ${isMobile ? "p-1" : "p-6"}`}>
                  {addressHidden ? (
                    <HiddenAddressMapPlaceholder />
                  ) : listing.latitude && listing.longitude ? (
                    <div className="w-full h-80 z-0">
                      <LeafletMap
                        latitude={listing.latitude}
                        longitude={listing.longitude}
                        address={listing.formattedAddress}
                      />
                    </div>
                  ) : null}
                  <div
                    className={`${
                      isMobile ? "text-sm" : "text-2xl"
                    } font-bold flex items-center gap-2 leading-none tracking-tight`}
                  >
                    <FaLocationDot className="h-6 w-6 text-secondary" />
                    {listing.formattedAddress}
                  </div>
                  {listing.mlsPropertyType && (
                    <div className="flex items-center pt-1">
                      <Badge variant="secondary">
                        {mlsListingTypeLabel(listing.mlsPropertyType)}
                      </Badge>
                    </div>
                  )}
                </div>
                <div className={`space-y-6 ${isMobile ? "p-1 pt-0" : "p-6 pt-0"}`}> 
                  {!mlsApproved && (
                    <>
                      {/* Description Field - First and Required */}
                      <div>
                        <InputField
                          label="Property Description"
                          name="legalDescription"
                          placeholder="Enter a detailed description of the property..."
                          value={listing?.legalDescription || ""}
                          onChange={(e) => {
                            const { value } = e.target;
                            setCreatedListing((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    legalDescription: value,
                                  }
                                : prev,
                            );
                          }}
                          type="textarea"
                          rows={8}
                          className="w-full"
                          required
                        />
                        {(!listing?.legalDescription ||
                          listing?.legalDescription?.trim() === "") && (
                          <p className="mt-2 text-sm text-red-600">
                            * Description is required to continue
                          </p>
                        )}
                      </div>
                      {listing.propertyType ? (
                        <>
                          {!mlsApproved ? (
                            <InputField
                              label="Property Type"
                              name="propertyType"
                              placeholder="Ex: Single Family"
                              type="text"
                              value={listing?.propertyType}
                              onChange={(e) => {
                                const { value } = e.target;
                                setCreatedListing((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        bedrooms: value,
                                      }
                                    : prev,
                                );
                              }}
                            />
                          ) : (
                            <div className="flex items-center gap-2">
                              <BsHousesFill className="h-6 w-6 text-secondary" />
                              <span>{listing.propertyType}</span>
                            </div>
                          )}
                        </>
                      ) : null}
                    </>
                  )}

                  <div className={`grid gap-4 ${isMobile ? "grid-cols-1" : "grid-cols-2"}`}>
                    {!mlsApproved ? (
                      <div className="space-y-2">
                        <InputField
                          label="Bedrooms"
                          name="bedrooms"
                          placeholder="Number of bedrooms"
                          type="number"
                          value={listing?.bedrooms || ""}
                          onChange={(e) => {
                            const { value } = e.target;
                            setCreatedListing((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    bedrooms: value,
                                  }
                                : prev,
                            );
                          }}
                        />
                      </div>
                    ) : listing.bedrooms ? (
                      <div className="flex items-center gap-2">
                        <FaBed className="h-6 w-6 text-secondary" />
                        <span>{listing.bedrooms} Beds</span>
                      </div>
                    ) : null}
                    {!mlsApproved ? (
                      <div className="space-y-2">
                        <InputField
                          label="Full Bathrooms"
                          name="fullBathrooms"
                          placeholder="Number of full bathrooms"
                          type="number"
                          value={listing?.fullBathrooms || ""}
                          onChange={(e) => {
                            const { value } = e.target;
                            setCreatedListing((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    fullBathrooms: parseFloat(value) || 0,
                                  }
                                : prev,
                            );
                          }}
                        />
                      </div>
                    ) : listing.fullBathrooms ? (
                      <div className="flex items-center gap-2">
                        <FaBath className="h-6 w-6 text-secondary" />
                        <span>{listing.fullBathrooms} Full Baths</span>
                      </div>
                    ) : null}
                    {!mlsApproved ? (
                      <div className="space-y-2">
                        <InputField
                          label="Half Bathrooms"
                          name="halfBathrooms"
                          placeholder="Number of half bathrooms"
                          type="number"
                          value={listing?.halfBathrooms || ""}
                          onChange={(e) => {
                            const { value } = e.target;
                            setCreatedListing((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    halfBathrooms: parseFloat(value) || 0,
                                  }
                                : prev,
                            );
                          }}
                        />
                      </div>
                    ) : listing.halfBathrooms ? (
                      <div className="flex items-center gap-2">
                        <FaToilet className="h-6 w-6 text-secondary" />
                        <span>{listing.halfBathrooms} Half Baths</span>
                      </div>
                    ) : null}
                    {!mlsApproved ? (
                      <div className="space-y-2">
                        <InputField
                          label="Square Footage"
                          name="squareFootage"
                          placeholder="Square footage"
                          type="number"
                          value={listing?.squareFootage || ""}
                          onChange={(e) => {
                            const { value } = e.target;
                            setCreatedListing((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    squareFootage: value,
                                  }
                                : prev,
                            );
                          }}
                        />
                      </div>
                    ) : listing.squareFootage ? (
                      <div className="flex items-center gap-2">
                        <MdSquareFoot className="h-6 w-6 text-secondary" />
                        <span>{listing.squareFootage} sq ft</span>
                      </div>
                    ) : null}
                    {!mlsApproved ? (
                      <div className="space-y-2">
                        <InputField
                          label="Lot Size (acres)"
                          name="lotSize"
                          placeholder="Lot size in acres"
                          type="number"
                          value={
                            listing?.lotSize
                              ? (
                                  Number.parseInt(listing.lotSize) / 43560
                                ).toFixed(2)
                              : ""
                          }
                          onChange={(e) => {
                            const { value } = e.target;
                            const sqFeet = parseFloat(value) * 43560;
                            setCreatedListing((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    lotSize: sqFeet.toString(),
                                  }
                                : prev,
                            );
                          }}
                        />
                      </div>
                    ) : listing.lotSize ? (
                      <div className="flex items-center gap-2">
                        <PiMapPinSimpleAreaFill className="h-6 w-6 text-secondary" />
                        <span>
                          {mlsApproved
                            ? listing.lotSize
                            : (
                                Number.parseInt(listing.lotSize) / 43560
                              ).toFixed(2)}{" "}
                          acres
                        </span>
                      </div>
                    ) : null}
                    {!mlsApproved ? (
                      <div className="space-y-2">
                        <InputField
                          label="Year Built"
                          name="yearBuilt"
                          placeholder="Year built"
                          type="number"
                          value={listing?.yearBuilt || ""}
                          onChange={(e) => {
                            const { value } = e.target;
                            setCreatedListing((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    yearBuilt: value,
                                  }
                                : prev,
                            );
                          }}
                        />
                      </div>
                    ) : listing.yearBuilt ? (
                      <div className="flex items-center gap-2">
                        <FaCalendarDays className="h-6 w-6 text-secondary" />
                        <span>Built {listing.yearBuilt}</span>
                      </div>
                    ) : null}
                    {!mlsApproved && listing.lastSaleDate ? (
                      <div className={`flex items-center gap-2 ${isMobile ? "order-last" : ""}`}>
                        <FaRegClock className="h-6 w-6 text-secondary" />
                        <span>
                          Last Sale Date:{" "}
                          {new Date(listing.lastSaleDate).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            },
                          )}
                        </span>
                      </div>
                    ) : null}
                    {!mlsApproved ? (
                      <div className="space-y-2">
                        <InputField
                          label="Price"
                          name="currentPrice"
                          placeholder="Price"
                          type="text"
                          value={listing?.currentPrice || ""}
                          onChange={(e) => {
                            const { value } = e.target;
                            setCreatedListing((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    currentPrice:
                                      parseFloat(
                                        value.replace(/[^0-9.]/g, ""),
                                      ) || 0,
                                  }
                                : prev,
                            );
                          }}
                          price={true}
                        />
                        {(!listing?.currentPrice ||
                          listing?.currentPrice <= 0) && (
                          <p className="mt-2 text-sm text-red-600">
                            * Price is required to continue
                          </p>
                        )}
                      </div>
                    ) : listing.currentPrice && listing.currentPrice > 0 ? (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <MdOutlinePriceChange className="h-6 w-6 text-secondary" />
                          <span>
                            {mlsApproved
                              ? `${priceRowLabel}: `
                              : `Last Sale Price: `}
                            ${" "}
                            {listing?.currentPrice.toLocaleString()}
                            {/* MB-1472: the number rendered here is currentPrice,
                            so "/month" is only true when the classified monthly
                            rate IS that number. When the record carries a lease
                            rate that is a different number, currentPrice is the
                            sale price - suffixing it produced "695,000/month",
                            and leaving it bare read as a $695,000 rent. Both
                            numbers get their own labelled line instead. */}
                            {listing?.leaseMonthlyDisplay != null &&
                              listing?.leaseMonthlyDisplay ===
                                listing?.currentPrice &&
                              "/month"}
                          </span>
                          {/* MB-1472: never invent a suffix - only label as "For Lease"
                          when the api couldn't classify an explicit monthly amount */}
                          {listing?.leaseMonthlyDisplay == null &&
                            listing?.mlsPropertyType === "CommercialLease" && (
                              <Badge variant="secondary">For Lease</Badge>
                            )}
                        </div>
                        {carriesBothPrices && (
                          <div className="flex items-center gap-2">
                            <MdOutlinePriceChange className="h-6 w-6 text-secondary" />
                            <span>
                              Lease Price: $
                              {listing?.leaseMonthlyDisplay?.toLocaleString()}
                              /month
                            </span>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                  {mlsApproved && listing.legalDescription ? (
                    <div>
                      <div className="flex">
                        <FaScroll className="h-6 w-6 text-secondary" />
                        <span className="ml-2">Description</span>
                      </div>
                      <p className="text-sm">{listing.legalDescription}</p>
                    </div>
                  ) : null}

                  {!mlsApproved ? (
                    <PropertyFeatureSelector
                      listing={listing}
                      setListing={setCreatedListing}
                    />
                  ) : (
                    <>
                      {listing.features &&
                        typeof listing.features === "object" &&
                        Object.keys(listing.features).length > 0 && (
                          <div>
                            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-primary">
                              <FaStar className="h-6 w-6 text-secondary" />
                              Features
                            </h3>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(listing.features).map(
                                ([key, value]) => {
                                  if (isBooleanField(key)) {
                                    if (value === true) {
                                      const relatedFields =
                                        booleanToFieldsMap[key] || [];
                                      return relatedFields.map((relatedKey) => {
                                        const relatedValue =
                                          listing.features[
                                            relatedKey as keyof (
                                              | Features
                                              | MLSFeatures
                                            )
                                          ];
                                        if (
                                          relatedValue !== undefined &&
                                          relatedValue !== null &&
                                          relatedValue !== ""
                                        ) {
                                          return (
                                            <div
                                              className="border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 px-2 py-1 rounded"
                                              key={relatedKey}
                                            >
                                              {`${formatKey(
                                                relatedKey,
                                              )}: ${relatedValue}`}
                                            </div>
                                          );
                                        }
                                        return null;
                                      });
                                    }
                                    return null;
                                  }

                                  if (isTypeOrAssociatedField(key)) {
                                    const correspondingBoolean =
                                      fieldToBooleanMap[key];
                                    if (
                                      correspondingBoolean &&
                                      listing.features[
                                        correspondingBoolean as keyof (
                                          | Features
                                          | MLSFeatures
                                        )
                                      ] === true
                                    ) {
                                      return null;
                                    }
                                    if (
                                      value !== undefined &&
                                      value !== null &&
                                      value !== ""
                                    ) {
                                      if (key === "garageSpaces") {
                                        return (
                                          <div
                                            className="border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 px-2 py-1 rounded"
                                            key={key}
                                          >
                                            {`Garage Spaces: ${value}`}
                                          </div>
                                        );
                                      }
                                      return (
                                        <div
                                          className="border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 px-2 py-1 rounded"
                                          key={key}
                                        >
                                          {`${formatKey(key)}: ${value}`}
                                        </div>
                                      );
                                    }
                                    return null;
                                  }

                                  if (
                                    value !== undefined &&
                                    value !== null &&
                                    value !== ""
                                  ) {
                                    return (
                                      <div
                                        className="border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 px-2 py-1 rounded"
                                        key={key}
                                      >
                                        {`${formatKey(key)}: ${String(
                                          value,
                                        ).replace(/,(\S)/g, ", $1")}`}
                                      </div>
                                    );
                                  }
                                  return null;
                                },
                              )}
                            </div>
                          </div>
                        )}
                    </>
                  )}

                  {listing.footer && (
                    <div className="mt-6 p-6 border-t">
                      <h3 className="text-lg font-semibold mb-4">
                        Agent Information
                      </h3>
                      <div className="space-y-4">
                        <>
                          {sharedListing && (
                            <>
                              <InputField
                                label="Listing Agent Name"
                                placeholder="Ex: John Doe"
                                type="text"
                                value={listing.footer.sharedAgentName || ""}
                                onChange={(e) =>
                                  handleAgentDetailChange(
                                    "sharedAgentName",
                                    e.target.value,
                                  )
                                }
                              />

                              <InputField
                                label="Listing Agent Phone"
                                placeholder="Ex: XXX-XXX-XXXX"
                                type="text"
                                value={listing.footer.sharedAgentPhone || ""}
                                onChange={(e) =>
                                  handleAgentDetailChange(
                                    "sharedAgentPhone",
                                    e.target.value,
                                  )
                                }
                              />

                              <InputField
                                label="Listing Agent Agency"
                                placeholder="Ex: Best Realty"
                                type="text"
                                value={listing.footer.sharedAgentAgency || ""}
                                onChange={(e) =>
                                  handleAgentDetailChange(
                                    "sharedAgentAgency",
                                    e.target.value,
                                  )
                                }
                              />

                              <InputField
                                label={"Listing Agent Agency Phone"}
                                placeholder="Ex: 555-123-4567"
                                type="text"
                                value={listing.footer.agencyPhone || ""}
                                onChange={(e) =>
                                  handleAgentDetailChange(
                                    "agencyPhone",
                                    e.target.value,
                                  )
                                }
                              />
                            </>
                          )}

                          <InputField
                            label={`${
                              sharedListing ? "Co-Marketing" : "Listing"
                            } Agent Name`}
                            placeholder="Ex: John Doe"
                            type="text"
                            value={listing.footer.agentName || ""}
                            onChange={(e) =>
                              handleAgentDetailChange(
                                "agentName",
                                e.target.value,
                              )
                            }
                          />

                          <InputField
                            label={`${
                              sharedListing ? "Co-Marketing" : "Listing"
                            } Agent Phone`}
                            placeholder="Ex: XXX-XXX-XXXX"
                            type="text"
                            value={listing.footer.agentPhone || ""}
                            onChange={(e) =>
                              handleAgentDetailChange(
                                "agentPhone",
                                e.target.value,
                              )
                            }
                          />

                          <InputField
                            label={`${
                              sharedListing ? "Co-Marketing" : "Listing"
                            } Agent Agency`}
                            placeholder="Ex: Best Realty"
                            type="text"
                            value={listing.footer.agentAgency || ""}
                            onChange={(e) =>
                              handleAgentDetailChange(
                                "agentAgency",
                                e.target.value,
                              )
                            }
                          />
                          {!sharedListing && (
                            <InputField
                              label={"Listing Agent Agency Phone"}
                              placeholder="Ex: 555-123-4567"
                              type="text"
                              value={listing.footer.agencyPhone || ""}
                              onChange={(e) =>
                                handleAgentDetailChange(
                                  "agencyPhone",
                                  e.target.value,
                                )
                              }
                            />
                          )}

                          <InputField
                            label="Source MLS"
                            placeholder="Ex: WestPenn"
                            type="text"
                            disabled={mlsApproved !== null && mlsApproved}
                            value={listing.footer.sourceMLS || ""}
                            onChange={(e) =>
                              handleAgentDetailChange(
                                "sourceMLS",
                                e.target.value,
                              )
                            }
                          />
                          <InputField
                            label="Source MLS ID"
                            placeholder="Ex: 1234567"
                            type="text"
                            value={listing.footer.sourceMLSId || ""}
                            disabled={mlsApproved !== null && mlsApproved}
                            onChange={(e) =>
                              handleAgentDetailChange(
                                "sourceMLSId",
                                e.target.value,
                              )
                            }
                          />
                        </>
                        {/* mls flow cherry picked commented by meet to match mls qa flow and non mls dev/ qa flow for agent info  */}
                        {/* {!mlsApproved ? (
                                                <>
                                                    {sharedListing && (
                                                        <>
                                                            <InputField
                                                                label="Listing Agent Name"
                                                                placeholder="Ex: John Doe"
                                                                type="text"
                                                                value={
                                                                    listing
                                                                        .footer
                                                                        .sharedAgentName ||
                                                                    ""
                                                                }
                                                                onChange={(e) =>
                                                                    handleAgentDetailChange(
                                                                        "sharedAgentName",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                            />

                                                            <InputField
                                                                label="Listing Agent Phone"
                                                                placeholder="Ex: XXX-XXX-XXXX"
                                                                type="text"
                                                                value={
                                                                    listing
                                                                        .footer
                                                                        .sharedAgentPhone ||
                                                                    ""
                                                                }
                                                                onChange={(e) =>
                                                                    handleAgentDetailChange(
                                                                        "sharedAgentPhone",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                            />

                                                            <InputField
                                                                label="Listing Agent Agency"
                                                                placeholder="Ex: Best Realty"
                                                                type="text"
                                                                value={
                                                                    listing
                                                                        .footer
                                                                        .sharedAgentAgency ||
                                                                    ""
                                                                }
                                                                onChange={(e) =>
                                                                    handleAgentDetailChange(
                                                                        "sharedAgentAgency",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                            />

                                                            <InputField
                                                                label={
                                                                    "Listing Agent Agency Phone"
                                                                }
                                                                placeholder="Ex: 555-123-4567"
                                                                type="text"
                                                                value={
                                                                    listing
                                                                        .footer
                                                                        .agencyPhone ||
                                                                    ""
                                                                }
                                                                onChange={(e) =>
                                                                    handleAgentDetailChange(
                                                                        "agencyPhone",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                            />
                                                        </>
                                                    )}

                                                    <InputField
                                                        label={`${
                                                            sharedListing
                                                                ? "Co-Marketing"
                                                                : "Listing"
                                                        } Agent Name`}
                                                        placeholder="Ex: John Doe"
                                                        type="text"
                                                        value={
                                                            listing.footer
                                                                .agentName || ""
                                                        }
                                                        onChange={(e) =>
                                                            handleAgentDetailChange(
                                                                "agentName",
                                                                e.target.value
                                                            )
                                                        }
                                                    />

                                                    <InputField
                                                        label={`${
                                                            sharedListing
                                                                ? "Co-Marketing"
                                                                : "Listing"
                                                        } Agent Phone`}
                                                        placeholder="Ex: XXX-XXX-XXXX"
                                                        type="text"
                                                        value={
                                                            listing.footer
                                                                .agentPhone ||
                                                            ""
                                                        }
                                                        onChange={(e) =>
                                                            handleAgentDetailChange(
                                                                "agentPhone",
                                                                e.target.value
                                                            )
                                                        }
                                                    />

                                                    <InputField
                                                        label={`${
                                                            sharedListing
                                                                ? "Co-Marketing"
                                                                : "Listing"
                                                        } Agent Agency`}
                                                        placeholder="Ex: Best Realty"
                                                        type="text"
                                                        value={
                                                            listing.footer
                                                                .agentAgency ||
                                                            ""
                                                        }
                                                        onChange={(e) =>
                                                            handleAgentDetailChange(
                                                                "agentAgency",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                    {!sharedListing && (
                                                        <InputField
                                                            label={
                                                                "Listing Agent Agency Phone"
                                                            }
                                                            placeholder="Ex: 555-123-4567"
                                                            type="text"
                                                            value={
                                                                listing.footer
                                                                    .agencyPhone ||
                                                                ""
                                                            }
                                                            onChange={(e) =>
                                                                handleAgentDetailChange(
                                                                    "agencyPhone",
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                        />
                                                    )}

                                                    <InputField
                                                        label="Source MLS"
                                                        placeholder="Ex: WestPenn"
                                                        type="text"
                                                        disabled={
                                                            mlsApproved !==
                                                                null &&
                                                            mlsApproved
                                                        }
                                                        value={
                                                            listing.footer
                                                                .sourceMLS || ""
                                                        }
                                                        onChange={(e) =>
                                                            handleAgentDetailChange(
                                                                "sourceMLS",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                    <InputField
                                                        label="Source MLS ID"
                                                        placeholder="Ex: 1234567"
                                                        type="text"
                                                        value={
                                                            listing.footer
                                                                .sourceMLSId ||
                                                            ""
                                                        }
                                                        disabled={
                                                            mlsApproved !==
                                                                null &&
                                                            mlsApproved
                                                        }
                                                        onChange={(e) =>
                                                            handleAgentDetailChange(
                                                                "sourceMLSId",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    {sharedListing &&
                                                        listing.footer
                                                            .sharedAgentName && (
                                                            <p>
                                                                <strong>
                                                                    Listing
                                                                    Agent Name:
                                                                </strong>{" "}
                                                                {
                                                                    listing
                                                                        .footer
                                                                        .sharedAgentName
                                                                }
                                                            </p>
                                                        )}
                                                    {sharedListing &&
                                                        listing.footer
                                                            .sharedAgentPhone && (
                                                            <p>
                                                                <strong>
                                                                    Listing
                                                                    Agent Phone:
                                                                </strong>{" "}
                                                                {
                                                                    listing
                                                                        .footer
                                                                        .sharedAgentPhone
                                                                }
                                                            </p>
                                                        )}
                                                    {sharedListing &&
                                                        listing.footer
                                                            .sharedAgentAgency && (
                                                            <p>
                                                                <strong>
                                                                    Listing
                                                                    Agent
                                                                    Agency:
                                                                </strong>{" "}
                                                                {
                                                                    listing
                                                                        .footer
                                                                        .sharedAgentAgency
                                                                }
                                                            </p>
                                                        )}
                                                    {listing.footer
                                                        .agentName && (
                                                        <p>
                                                            <strong>
                                                                {sharedListing
                                                                    ? "Co-Marketing"
                                                                    : "Listing"}{" "}
                                                                Agent Name:
                                                            </strong>{" "}
                                                            {
                                                                listing.footer
                                                                    .agentName
                                                            }
                                                        </p>
                                                    )}
                                                    {listing.footer
                                                        .agentPhone && (
                                                        <p>
                                                            <strong>
                                                                {sharedListing
                                                                    ? "Co-Marketing"
                                                                    : "Listing"}{" "}
                                                                Agent Phone:
                                                            </strong>{" "}
                                                            {
                                                                listing.footer
                                                                    .agentPhone
                                                            }
                                                        </p>
                                                    )}
                                                    {listing.footer
                                                        .agentAgency && (
                                                        <p>
                                                            <strong>
                                                                {sharedListing
                                                                    ? "Co-Marketing"
                                                                    : "Listing"}{" "}
                                                                Agent Agency:
                                                            </strong>{" "}
                                                            {
                                                                listing.footer
                                                                    .agentAgency
                                                            }
                                                        </p>
                                                    )}
                                                    {listing.footer
                                                        .sourceMLS && (
                                                        <p>
                                                            <strong>
                                                                Source MLS:
                                                            </strong>{" "}
                                                            {
                                                                listing.footer
                                                                    .sourceMLS
                                                            }
                                                        </p>
                                                    )}
                                                    {listing.footer
                                                        .sourceMLSId && (
                                                        <p>
                                                            <strong>
                                                                Source MLS ID:
                                                            </strong>{" "}
                                                            {
                                                                listing.footer
                                                                    .sourceMLSId
                                                            }
                                                        </p>
                                                    )}
                                                </>
                                            )} */}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          {!showCandidatePicker && (
          <div
            className={`w-full flex justify-between mt-8 ${
              isMobile ? "gap-4 pb-8" : "gap-20"
            }`}
          >
            <Button
              id="confirm-listing-back-btn"
              className={`${isMobile ? "w-full" : "w-1/3"}`}
              onClick={handleBack}
            >
              Back
            </Button>
            {listing?.mlsStatus === "Active" ||
            listing?.mlsStatus === "ActiveUnderContract" ||
            listing?.mlsStatus === "ComingSoon" ||
            !mlsApproved ? (
              <Button
                id="confirm-listing-save-btn"
                className={`${isMobile ? "w-full" : "w-1/3"}`}
                onClick={handleSaveAndNext}
                disabled={
                  listing?.mlsStatus !== "Active" &&
                  listing?.mlsStatus !== "ActiveUnderContract" &&
                  listing?.mlsStatus !== "ComingSoon" &&
                  !!mlsApproved
                }
              >
                Next
              </Button>
            ) : null}
          </div>
          )}
          {!showCandidatePicker &&
          mlsApproved &&
          listing?.mlsStatus !== "Active" &&
          listing?.mlsStatus !== "ActiveUnderContract" &&
          listing?.mlsStatus !== "ComingSoon" ? (
            <>
              {listing?.mlsStatus === "Closed" ? (
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-500">
                    This listing is closed. If the property was recently
                    relisted, the new listing can take time to appear in the
                    MLS feed - try again later, or import it using the MLS
                    number. If you want to create a congratulations/sold post,
                    please go to the Schedule and create an Ad Hoc post.
                  </p>
                </div>
              ) : (
                <div className="mt-4 text-center">
                  {listing?.mlsStatus === "EXISTS" || listing?.exists ? (
                    <p className="text-sm text-gray-500">
                      This listing already exists. Please remove the existing
                      listing before proceeding.
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500">
                      This listing is marked as {listing?.mlsStatus}. Please
                      update the MLS before proceeding.
                    </p>
                  )}
                </div>
              )}
            </>
          ) : null}
        </section>
        <ReconfirmOverlay
          isOpen={descPromptOpen}
          title="Add more description?"
          message="Posts with richer property descriptions tend to perform better on social media."
          messageTwo="Would you like to add more details before continuing?"
          confirmText="Yes, add more"
          cancelText="No, continue"
          onConfirm={async () => {
            setDescPromptOpen(false);
            scrollToDescription(true);
          }}
          onClose={() => {
            setDescPromptOpen(false);
          }}
          cancelClose={() => {
            setDescPromptOpen(false);
            // Ensure price is provided before continuing for non-MLS listings
            if (
              !mlsApproved &&
              (!listing?.currentPrice || listing.currentPrice <= 0)
            ) {
              scrollToField("currentPrice");
              return;
            }
            handleNONMLSContinue();
          }}
        />
      </main>
    );

    // Handler for updating agent details
    function handleAgentDetailChange(
        field: keyof ComfirmListing["footer"],
        value: string
    ) {
        setCreatedListing((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                footer: {
                    ...prev.footer,
                    [field]: value,
                },
            };
        });
    }
};

export default ConfirmUserListing;
